// Dividend receivables / payables — the operator view (Performance tab, single-account scope).
//
// Every dividend event the account carries under the two-stage lifecycle: sleeve accruals (net of
// withholding, recovered from the stored snapshot maps), the central-book short's GROSS payable, and the
// fractional reserve's residual — each with its ex-date, pay date (or "unknown → auto-settles ex+90"),
// settle date and stage (open = ex'd but not yet cash; settled = in cash on the pay date or the ex+90
// cutoff). Open Σ is the NAV receivable line (< 0 = payable); settled Σ sits inside net cash. Read-only;
// renders a one-line notice while the lifecycle seam is off. House rule: sortable by any column.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

const usd = (v) => (v == null || isNaN(v) ? '—' : `${v < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const sign = (v) => (v == null || isNaN(v) ? 'text-gray-400' : v >= 0 ? 'text-emerald-500' : 'text-red-500');
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`);
const LEG = { strategy: 'Sleeve', central: 'Central book', reserve: 'Reserve' };

const COLS = [
  { key: 'leg', type: 'string', get: (r) => r.leg },
  { key: 'symbol', type: 'string', get: (r) => r.symbol },
  { key: 'strategy', type: 'string', get: (r) => r.strategy || '' },
  { key: 'ex_date', type: 'string', get: (r) => r.ex_date },
  { key: 'qty', type: 'abs', get: (r) => r.qty },
  { key: 'gross', type: 'abs', get: (r) => r.gross ?? (r.qty != null && r.gross_per_share != null ? r.qty * r.gross_per_share : null) },
  { key: 'rate', type: 'number', get: (r) => r.rate },
  { key: 'net', type: 'abs', get: (r) => r.net },
  { key: 'pay_date', type: 'string', get: (r) => r.pay_date || '' },
  { key: 'settle_date', type: 'string', get: (r) => r.settle_date || '' },
  { key: 'stage', type: 'string', get: (r) => r.stage },
];

export default function DividendsPanel({ accountId }) {
  const [open, setOpen] = useState(true);
  const [stageFilter, setStageFilter] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accountId == null) { setData(null); return undefined; }
    let alive = true;
    setLoading(true); setError(null);
    tradingService.getDividendEvents(accountId)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.error || 'Failed to load dividend events'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountId]);

  const events = useMemo(() => data?.events || [], [data]);
  const filtered = useMemo(() => events.filter((e) => (
    stageFilter === 'all' ? true : stageFilter === 'open' ? e.stage === 'open' : e.stage !== 'open'
  )), [events, stageFilter]);
  const { sorted, toggle, indicator } = useSortableRows(filtered, COLS, { key: 'net', dir: 'desc' });
  const totals = data?.totals || {};

  if (accountId == null) return null;
  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} className="text-left">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Dividends · receivables &amp; payables</div>
          <div className="text-sm font-serif text-gray-800 dark:text-gray-200">
            Open <span className={`tabular-nums ${sign(totals.open)}`}>{usd(totals.open)}</span>
            <span className="text-gray-400 text-xs"> · settled </span><span className={`tabular-nums ${sign(totals.settled)}`}>{usd(totals.settled)}</span>
            <span className="text-gray-400 text-xs">{data?.as_of ? ` · as of ${data.as_of}` : ''}</span>
          </div>
        </button>
        <div className="flex items-center gap-1 text-[10px]">
          {['open', 'settled', 'all'].map((k) => (
            <button key={k} onClick={() => setStageFilter(k)}
              className={`px-2 py-0.5 rounded border ${stageFilter === k ? 'border-brand-gold text-brand-gold' : 'border-gray-300 dark:border-gray-700 text-gray-500'}`}>
              {k}
            </button>
          ))}
        </div>
      </div>
      {open && (
        <div className="mt-3">
          {data && data.enabled === false && (
            <p className="text-xs text-gray-400 italic">Dividend lifecycle is off — accruals are gross and ex-date only; no receivable / payable split is kept.</p>
          )}
          {loading && <p className="text-xs text-gray-400">Loading…</p>}
          {error && <p className="text-xs text-red-500">{error}</p>}
          {data && data.enabled && (
            <>
              <div className="grid grid-cols-3 gap-2 text-[11px] mb-2">
                {['strategies', 'central', 'reserve'].map((k) => (
                  <div key={k} className="rounded border border-gray-200 dark:border-gray-800 p-2">
                    <div className="uppercase tracking-wider text-gray-400 text-[9px]">{k === 'strategies' ? 'Sleeves (net)' : k === 'central' ? 'Central book (gross payable)' : 'Reserve residual'}</div>
                    <div className="tabular-nums">open <span className={sign(totals.by_leg?.[k]?.open)}>{usd(totals.by_leg?.[k]?.open)}</span>
                      <span className="text-gray-400"> · settled </span><span className={sign(totals.by_leg?.[k]?.settled)}>{usd(totals.by_leg?.[k]?.settled)}</span>
                      <span className="text-gray-400"> · {totals.by_leg?.[k]?.n ?? 0} ev.</span></div>
                  </div>
                ))}
              </div>
              {data.skipped && data.skipped.length > 0 && (
                <p className="text-[10px] text-amber-500 mb-2">
                  Reserve rows skipped (|shares| &gt; 0.5 — artefacts, never a payable): {data.skipped.map((x) => `${x.symbol} ${x.ex_date} (${Number(x.qty).toFixed(3)} sh)`).join(', ')}
                </p>
              )}
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-[11px] tabular-nums">
                  <thead className="text-[9px] uppercase tracking-wider text-gray-400">
                    <tr>
                      <SortableTh label="Leg" colKey="leg" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Symbol" colKey="symbol" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Sleeve" colKey="strategy" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Ex-date" colKey="ex_date" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Qty" colKey="qty" toggle={toggle} indicator={indicator} />
                      <SortableTh label="Gross" colKey="gross" toggle={toggle} indicator={indicator} />
                      <SortableTh label="WHT" colKey="rate" toggle={toggle} indicator={indicator} />
                      <SortableTh label="Net" colKey="net" toggle={toggle} indicator={indicator} />
                      <SortableTh label="Pay date" colKey="pay_date" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Settles" colKey="settle_date" toggle={toggle} indicator={indicator} align="left" />
                      <SortableTh label="Stage" colKey="stage" toggle={toggle} indicator={indicator} align="left" />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r, i) => {
                      const gross = r.gross ?? (r.qty != null && r.gross_per_share != null ? r.qty * r.gross_per_share : null);
                      return (
                        <tr key={`${r.leg}-${r.strategy_id || ''}-${r.symbol}-${r.ex_date}-${i}`} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="py-1 text-gray-500">{LEG[r.leg] || r.leg}</td>
                          <td className="py-1 font-medium text-gray-800 dark:text-gray-200">{r.symbol}</td>
                          <td className="py-1 text-gray-500 truncate max-w-[160px]">{r.strategy || ''}</td>
                          <td className="py-1">{r.ex_date}</td>
                          <td className={`py-1 text-right ${r.qty != null ? sign(r.qty) : ''}`}>{r.qty != null ? Number(r.qty).toFixed(r.leg === 'reserve' ? 4 : 2) : '—'}</td>
                          <td className={`py-1 text-right ${sign(gross)}`}>{usd(gross)}</td>
                          <td className="py-1 text-right text-gray-500">{pct(r.rate)}</td>
                          <td className={`py-1 text-right ${sign(r.net)}`}>{usd(r.net)}</td>
                          <td className="py-1">{r.pay_date || <span className="text-amber-500">unknown → auto</span>}</td>
                          <td className="py-1">{r.settle_date || '—'}</td>
                          <td className={`py-1 ${r.stage === 'open' ? 'text-amber-500' : 'text-gray-500'}`}>{r.stage}</td>
                        </tr>
                      );
                    })}
                    {!sorted.length && <tr><td colSpan={11} className="py-2 text-gray-400 italic">No {stageFilter === 'all' ? '' : stageFilter} events.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
